import { useState, useRef, useEffect } from "react";
import { Sparkles, Mic, Send, X, Loader2, ArrowRight, History, RotateCcw, Power } from "lucide-react";
import { api, apiError } from "@/lib/api";
import { KMark } from "@/components/KLogo";
import { toast } from "sonner";

const SUGGESTIONS = [
  "Qual o horário da piscina?",
  "Posso fazer obra no sábado?",
  "Tem churrasqueira sábado às 18h?",
  "Quando vence meu boleto?",
];

const fmt = (iso) => new Date(iso).toLocaleString("pt-BR", { day: "2-digit", month: "2-digit", hour: "2-digit", minute: "2-digit", timeZone: "America/Sao_Paulo" });

export default function AIAssistant() {
  const [open, setOpen] = useState(false);
  const [messages, setMessages] = useState([]);
  const [input, setInput] = useState("");
  const [loading, setLoading] = useState(false);
  const [sessionId, setSessionId] = useState(null);
  const [showHistory, setShowHistory] = useState(false);
  const [history, setHistory] = useState([]);
  const scrollRef = useRef(null);
  const inputRef = useRef(null);

  useEffect(() => {
    scrollRef.current?.scrollTo({ top: scrollRef.current.scrollHeight, behavior: "smooth" });
  }, [messages, loading]);

  useEffect(() => {
    if (open) setTimeout(() => inputRef.current?.focus(), 250);
  }, [open]);

  const ask = async (question) => {
    const q = (question || input).trim();
    if (!q || loading) return;
    setInput("");
    setShowHistory(false);
    setMessages((m) => [...m, { role: "user", text: q }]);
    setLoading(true);
    try {
      const { data } = await api.post("/ai/ask", { question: q, session_id: sessionId });
      setSessionId(data.session_id);
      setMessages((m) => [...m, { role: "ai", text: data.answer, fallback: data.fallback, question: q }]);
    } catch (e) {
      toast.error(apiError(e));
    } finally {
      setLoading(false);
      setTimeout(() => inputRef.current?.focus(), 100);
    }
  };

  const forward = async (question) => {
    try {
      await api.post("/ai/forward", { question });
      toast.success("Pergunta enviada para a administração.");
      setMessages((m) => m.map((x) => (x.question === question ? { ...x, forwarded: true } : x)));
    } catch (e) {
      toast.error(apiError(e));
    }
  };

  const newQuestion = () => {
    setInput("");
    setTimeout(() => inputRef.current?.focus(), 50);
  };

  const endChat = () => {
    setMessages([]);
    setSessionId(null);
    setInput("");
    setShowHistory(false);
  };

  const loadHistory = async () => {
    if (showHistory) return setShowHistory(false);
    try {
      const { data } = await api.get("/ai/history");
      setHistory(data.slice(0, 15));
      setShowHistory(true);
    } catch (e) {
      toast.error(apiError(e));
    }
  };

  const openConversation = (item) => {
    setSessionId(item.session_id);
    setMessages([
      { role: "user", text: item.question },
      { role: "ai", text: item.answer, question: item.question },
    ]);
    setShowHistory(false);
    setTimeout(() => inputRef.current?.focus(), 100);
  };

  const hasChat = messages.length > 0;

  return (
    <>
      <button
        data-testid="ai-fab"
        onClick={() => setOpen(true)}
        className="fixed z-40 bottom-24 right-4 md:bottom-8 md:right-8 flex items-center gap-2 rounded-full brand-gradient ai-pulse px-4 py-3 md:px-5 md:py-4 text-white font-semibold active:scale-95 transition-transform"
      >
        <Sparkles className="w-5 h-5" />
        <span className="hidden sm:inline font-display text-sm">Pergunte ao KONDDO</span>
      </button>

      {open && (
        <div className="fixed inset-0 z-50 flex items-end md:items-center md:justify-end">
          <div className="absolute inset-0 bg-black/70 backdrop-blur-sm" onClick={() => setOpen(false)} />
          <div
            data-testid="ai-panel"
            className="relative w-full md:w-[440px] md:mr-8 md:rounded-3xl rounded-t-3xl glass-strong border border-white/10 h-[85dvh] md:h-[640px] flex flex-col overflow-hidden float-in pb-safe"
          >
            <div className="flex items-center justify-between p-4 border-b border-white/10">
              <div className="flex items-center gap-3">
                <KMark size={38} />
                <div>
                  <p className="font-display font-bold text-sm">Pergunte ao KONDDO</p>
                  <p className="text-xs text-muted-foreground">Assistente inteligente do seu condomínio</p>
                </div>
              </div>
              <button data-testid="ai-close" onClick={() => setOpen(false)} className="p-2 rounded-full hover:bg-white/10 transition-colors">
                <X className="w-5 h-5" />
              </button>
            </div>

            <div ref={scrollRef} className="flex-1 overflow-y-auto p-4 space-y-4">
              {!hasChat && !showHistory && (
                <div className="space-y-4">
                  <div className="text-center py-6">
                    <div className="inline-flex mb-3"><KMark size={56} /></div>
                    <p className="font-display font-semibold">Olá! Como posso ajudar?</p>
                    <p className="text-sm text-muted-foreground mt-1 px-4">Pergunte em linguagem natural sobre o seu condomínio.</p>
                  </div>
                  <div className="grid gap-2">
                    {SUGGESTIONS.map((s) => (
                      <button key={s} data-testid="ai-suggestion" onClick={() => ask(s)} className="text-left text-sm rounded-2xl px-4 py-3 bg-white/5 border border-white/10 hover:border-primary/40 hover:bg-white/[0.08] transition-colors flex items-center justify-between group">
                        {s}
                        <ArrowRight className="w-4 h-4 text-muted-foreground group-hover:text-primary transition-colors" />
                      </button>
                    ))}
                  </div>
                  <button data-testid="ai-history-btn" onClick={loadHistory} className="w-full flex items-center justify-center gap-2 text-xs text-muted-foreground hover:text-foreground transition-colors py-2">
                    <History className="w-4 h-4" /> Perguntas anteriores
                  </button>
                </div>
              )}

              {showHistory && (
                <div className="space-y-2">
                  <div className="flex items-center justify-between">
                    <p className="text-xs uppercase tracking-wider text-primary font-medium">Perguntas anteriores</p>
                    <button onClick={() => setShowHistory(false)} className="text-xs text-muted-foreground">Voltar</button>
                  </div>
                  {history.length === 0 && <p className="text-sm text-muted-foreground py-4 text-center">Nenhuma pergunta ainda.</p>}
                  {history.map((h) => (
                    <button key={h.id || h.created_at} data-testid="ai-history-item" onClick={() => openConversation(h)} className="w-full text-left rounded-2xl px-4 py-3 bg-white/5 border border-white/10 hover:border-primary/40 transition-colors">
                      <p className="text-sm font-medium truncate">{h.question}</p>
                      <p className="text-xs text-muted-foreground truncate mt-0.5">{fmt(h.created_at)}</p>
                    </button>
                  ))}
                </div>
              )}

              {messages.map((m, i) => (
                <div key={i} className={`flex ${m.role === "user" ? "justify-end" : "justify-start"}`}>
                  <div className={`max-w-[85%] rounded-2xl px-4 py-3 text-sm leading-relaxed ${m.role === "user" ? "brand-gradient text-white" : "bg-white/5 border border-white/10"}`}>
                    <p className="whitespace-pre-wrap">{m.text}</p>
                    {m.fallback && !m.forwarded && (
                      <button data-testid="ai-forward-btn" onClick={() => forward(m.question)} className="mt-3 w-full rounded-xl brand-gradient text-white text-xs font-semibold py-2 active:scale-95 transition-transform">
                        Enviar para administração
                      </button>
                    )}
                    {m.forwarded && <p className="mt-2 text-xs text-primary">✓ Enviado para a administração</p>}
                  </div>
                </div>
              ))}

              {loading && (
                <div className="flex justify-start">
                  <div className="rounded-2xl px-4 py-3 bg-white/5 border border-white/10 flex items-center gap-2">
                    <Loader2 className="w-4 h-4 animate-spin text-primary" />
                    <span className="text-xs text-muted-foreground">KONDDO está consultando...</span>
                  </div>
                </div>
              )}

              {hasChat && !loading && (
                <div className="flex gap-2 pt-1">
                  <button data-testid="ai-new-question" onClick={newQuestion} className="flex-1 flex items-center justify-center gap-1.5 rounded-full bg-white/5 border border-white/10 py-2 text-xs font-medium hover:bg-white/10 transition-colors">
                    <RotateCcw className="w-3.5 h-3.5" /> Fazer outra pergunta
                  </button>
                  <button data-testid="ai-end-chat" onClick={endChat} className="flex-1 flex items-center justify-center gap-1.5 rounded-full bg-white/5 border border-white/10 py-2 text-xs font-medium text-muted-foreground hover:text-foreground transition-colors">
                    <Power className="w-3.5 h-3.5" /> Encerrar atendimento
                  </button>
                </div>
              )}
            </div>

            <div className="p-3 border-t border-white/10">
              <div className="flex items-center gap-2 rounded-full bg-white/5 border border-white/10 pl-4 pr-2 py-1.5 focus-within:border-primary/50 transition-colors">
                <input
                  ref={inputRef}
                  data-testid="ai-input"
                  value={input}
                  onChange={(e) => setInput(e.target.value)}
                  onKeyDown={(e) => e.key === "Enter" && ask()}
                  placeholder="Escreva sua pergunta..."
                  className="flex-1 bg-transparent outline-none text-sm placeholder:text-muted-foreground"
                />
                <button data-testid="ai-mic" className="p-2 rounded-full hover:bg-white/10 transition-colors" title="Em breve: voz">
                  <Mic className="w-4 h-4 text-muted-foreground" />
                </button>
                <button data-testid="ai-send" onClick={() => ask()} className="p-2 rounded-full brand-gradient text-white active:scale-90 transition-transform">
                  <Send className="w-4 h-4" />
                </button>
              </div>
            </div>
          </div>
        </div>
      )}
    </>
  );
}
